import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import Avatar from "../../components/Avatar";

const DAYS = [
  { v: "mon", l: "Mon" },
  { v: "tue", l: "Tue" },
  { v: "wed", l: "Wed" },
  { v: "thu", l: "Thu" },
  { v: "fri", l: "Fri" },
  { v: "sat", l: "Sat" },
  { v: "sun", l: "Sun" },
];

const emptySchedule = () => ({
  days: ["mon", "tue", "wed", "thu", "fri"],
  start: "09:00",
  end: "18:00",
});

export default function AdminPromoters() {
  const [promoters, setPromoters] = useState([]);
  const [stores, setStores] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    store_ids: [],
    project_ids: [],
    schedules: {}, // store_id -> { days:[], start, end }
  });
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const load = async () => {
    try {
      const [p, s, pr] = await Promise.all([
        api.get("/admin/promoters"),
        api.get("/admin/stores"),
        api.get("/admin/projects"),
      ]);
      setPromoters(p.data);
      setStores(s.data);
      setProjects(pr.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", phone: "", store_ids: [], project_ids: [], schedules: {} });
    setAvatarFile(null);
    setAvatarPreview(null);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      email: p.email,
      password: "",
      phone: p.phone || "",
      store_ids: p.store_ids || [],
      project_ids: p.project_ids || [],
      schedules: p.schedules || {},
    });
    setAvatarFile(null);
    setAvatarPreview(null);
    setOpen(true);
  };

  const onAvatarFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const uploadAvatar = async (promoterId) => {
    if (!avatarFile) return;
    const fd = new FormData();
    fd.append("photo", avatarFile);
    await api.post(`/admin/promoters/${promoterId}/avatar`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const toggleStore = (id) => {
    setForm((f) => {
      const exists = f.store_ids.includes(id);
      const store_ids = exists ? f.store_ids.filter((x) => x !== id) : [...f.store_ids, id];
      const schedules = { ...f.schedules };
      if (!exists && !schedules[id]) schedules[id] = emptySchedule();
      if (exists) delete schedules[id];
      return { ...f, store_ids, schedules };
    });
  };

  const toggleDay = (storeId, day) => {
    setForm((f) => {
      const cur = f.schedules[storeId] || emptySchedule();
      const days = cur.days.includes(day)
        ? cur.days.filter((d) => d !== day)
        : [...cur.days, day];
      return { ...f, schedules: { ...f.schedules, [storeId]: { ...cur, days } } };
    });
  };

  const setTime = (storeId, field, value) => {
    setForm((f) => ({
      ...f,
      schedules: {
        ...f.schedules,
        [storeId]: { ...(f.schedules[storeId] || emptySchedule()), [field]: value },
      },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const body = {
          name: form.name,
          phone: form.phone,
          store_ids: form.store_ids,
          project_ids: form.project_ids,
          schedules: form.schedules,
        };
        if (form.password) body.password = form.password;
        await api.patch(`/admin/promoters/${editing.id}`, body);
        if (avatarFile) await uploadAvatar(editing.id);
        toast.success("Promoter updated");
      } else {
        const { data: created } = await api.post("/admin/promoters", {
          name: form.name,
          email: form.email,
          password: form.password,
          phone: form.phone,
          project_ids: form.project_ids,
        });
        if (form.store_ids.length || form.project_ids.length) {
          await api.patch(`/admin/promoters/${created.id}`, {
            store_ids: form.store_ids,
            project_ids: form.project_ids,
            schedules: form.schedules,
          });
        }
        if (avatarFile) await uploadAvatar(created.id);
        toast.success("Promoter created");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete promoter ${p.name}?`)) return;
    try {
      await api.delete(`/admin/promoters/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button
          className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white"
          onClick={openCreate}
          data-testid="add-promoter-button"
        >
          <Plus className="h-4 w-4 mr-1" /> New Promoter
        </Button>
      </div>

      <div className="glass-panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/50 border-b border-slate-200/70">
            <tr>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Name</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Email</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Phone</th>
              <th className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Stores & Schedule</th>
              <th className="text-right p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="promoters-table-rows">
            {promoters.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-neutral-500">
                  No promoters yet.
                </td>
              </tr>
            ) : (
              promoters.map((p) => (
                <tr key={p.id} className="border-t border-slate-200/70 align-top">
                  <td className="p-3 font-medium">
                    <div className="flex items-center gap-3">
                      <Avatar path={p.avatar_path} name={p.name} size={36} />
                      <span>{p.name}</span>
                    </div>
                  </td>
                  <td className="p-3 font-mono text-xs">{p.email}</td>
                  <td className="p-3">{p.phone || "—"}</td>
                  <td className="p-3">
                    {(p.stores || []).length === 0 ? (
                      <span className="text-neutral-400">Unassigned</span>
                    ) : (
                      <div className="space-y-1">
                        {(p.stores || []).map((s) => {
                          const sch = p.schedules?.[s.id];
                          return (
                            <div key={s.id} className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="inline-block px-2 py-0.5 border border-slate-300/60 bg-white/50">
                                {s.name}
                              </span>
                              {sch ? (
                                <span className="font-mono text-[10px] text-neutral-500">
                                  {(sch.days || []).map((d) => d.toUpperCase()).join("·")}{" "}
                                  {sch.start} – {sch.end}
                                </span>
                              ) : (
                                <span className="font-mono text-[10px] text-neutral-400">
                                  any time
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-xl"
                      onClick={() => openEdit(p)}
                      data-testid={`edit-promoter-${p.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-xl text-red-600"
                      onClick={() => del(p)}
                      data-testid={`delete-promoter-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editing ? "Edit promoter" : "Create promoter"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Name</Label>
                <Input
                  className="mt-2 rounded-xl"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="promoter-form-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Phone</Label>
                <Input
                  className="mt-2 rounded-xl"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="promoter-form-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                className="mt-2 rounded-xl"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="promoter-form-email"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">
                {editing ? "New password (leave blank to keep)" : "Password"}
              </Label>
              <Input
                type="password"
                className="mt-2 rounded-xl font-mono"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="new-password"
                spellCheck={false}
                data-testid="promoter-form-password"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">
                Assign projects ({form.project_ids.length} selected)
              </Label>
              <div className="mt-2 border border-slate-300/60">
                <div className="max-h-40 overflow-y-auto" data-testid="promoter-form-projects-scroll">
                  {projects.length === 0 ? (
                    <div className="p-4 text-sm text-neutral-500">No projects. Create one first.</div>
                  ) : (
                    <div className="divide-y divide-slate-200/50">
                      {projects.map((p) => {
                        const checked = form.project_ids.includes(p.id);
                        return (
                          <label
                            key={p.id}
                            className="flex items-center gap-3 cursor-pointer p-3"
                            data-testid={`promoter-form-project-${p.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() =>
                                setForm((f) => ({
                                  ...f,
                                  project_ids: checked
                                    ? f.project_ids.filter((x) => x !== p.id)
                                    : [...f.project_ids, p.id],
                                }))
                              }
                              className="rounded-xl"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium">{p.name}</div>
                              <div className="text-xs font-mono text-neutral-500">
                                {p.start_date} → {p.end_date}
                                {p.status === "archived" && (
                                  <span className="ml-2 text-neutral-400 uppercase">archived</span>
                                )}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">
                Assign stores ({form.store_ids.length} selected)
              </Label>
              <div className="mt-2 border border-slate-300/60">
                <div
                  className="max-h-72 overflow-y-auto"
                  data-testid="promoter-form-stores-scroll"
                >
                  {stores.length === 0 ? (
                    <div className="p-4 text-sm text-neutral-500">No stores. Create one first.</div>
                  ) : (
                    <div className="divide-y divide-slate-200/50">
                      {stores.map((s) => {
                        const checked = form.store_ids.includes(s.id);
                        const sch = form.schedules[s.id] || emptySchedule();
                        return (
                          <div key={s.id} className="p-3">
                            <label
                              className="flex items-start gap-3 cursor-pointer"
                              data-testid={`promoter-form-store-${s.id}`}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => toggleStore(s.id)}
                                className="rounded-xl mt-0.5"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium">{s.name}</div>
                                <div className="text-xs text-neutral-500">{s.address}</div>
                              </div>
                            </label>
                            {checked && (
                              <div className="mt-3 pl-7 space-y-3 border-l border-slate-200/50 ml-1">
                                <div>
                                  <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                                    <Clock className="h-3 w-3" /> Working days
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {DAYS.map((d) => {
                                      const on = sch.days?.includes(d.v);
                                      return (
                                        <button
                                          type="button"
                                          key={d.v}
                                          onClick={() => toggleDay(s.id, d.v)}
                                          className={`px-2 py-1 text-[11px] font-mono border ${
                                            on
                                              ? "bg-slate-900 text-white border-black"
                                              : "bg-white text-neutral-600 border-slate-300/60 hover:bg-white/50"
                                          }`}
                                          data-testid={`day-${s.id}-${d.v}`}
                                        >
                                          {d.l}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-[10px] uppercase tracking-widest">
                                      Shift start
                                    </Label>
                                    <Input
                                      type="time"
                                      value={sch.start || ""}
                                      onChange={(e) => setTime(s.id, "start", e.target.value)}
                                      className="rounded-xl mt-1 font-mono"
                                      data-testid={`time-start-${s.id}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-[10px] uppercase tracking-widest">
                                      Shift end
                                    </Label>
                                    <Input
                                      type="time"
                                      value={sch.end || ""}
                                      onChange={(e) => setTime(s.id, "end", e.target.value)}
                                      className="rounded-xl mt-1 font-mono"
                                      data-testid={`time-end-${s.id}`}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              className="rounded-xl bg-slate-900 hover:bg-slate-700 text-white"
              disabled={saving}
              onClick={save}
              data-testid="promoter-form-save"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
